
import * as angular from "angular";
import "angular-material";

import { FormBuilderService } from "./formBuilder.service";
import { IForm } from "./IForm";
import { IFormDetail } from "./IFormDetail";
import { IKeyValuePair } from "@interfaces/IKeyValuePair";
import { SortType } from "@enums/SortType";
import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell } from "@directives/_veri/veriTable/veriTable.component";

import "./formBuilder.scss";

class FormBuilderController {

    forms: IForm[];

    tableHeaders: IVeriTableHeader[];
    tableRows: IVeriTableRow<IForm>[] = [];

    fieldTypes: IKeyValuePair[] = [];
    formTypes: IKeyValuePair[] = [];
    autoDataFields: IKeyValuePair[] = [];

    selectedFormId = 0;

    changesMade = false;
    createFormMode = false;
    saving = false;
    loading = false;

    createFormCategories = [];

    createForm: IForm;

    jobCompletionTypeFormId = -1;

    get showDetailsForm(): boolean {
        return !!this.selectedFormId || this.createFormMode;
    }

    get parentInputForm(): IForm {
        return this.createFormMode ? this.createForm : this.selectedForm;
    }

    selectedForm: IForm;

    get selectedFormDetails(): IFormDetail[] {
        const selectedForm = this.selectedForm;

        if (!selectedForm) {
            return null;
        }

        return selectedForm.FormFields;
    }

    get selectedFormCategories(): string[] {
        if (!this.forms || !this.selectedFormId) {
            return null;
        }

        const categories: string[] =
            this.selectedFormDetails.map(item => item.Category)
                .filter((value, index, self) => self.indexOf(value) === index);

        return categories;
    }

    static $inject = ["formBuilderService", "$mdDialog"];

    constructor(
        private readonly formBuilderService: FormBuilderService,
        private readonly $mdDialog: angular.material.IDialogService) {

        this.selectedFormId = 0;

        this.createFormMode = false;
        this.saving = false;

        this.initTableHeaders();
    }

    $onInit() {
        this.fieldTypes = this.formBuilderService.getFieldTypes();
        this.autoDataFields = this.formBuilderService.getAutoDataFields();
        this.retrieveData();
    }

    initTableHeaders(): void {
        this.tableHeaders = [
            {
                title: "Form Name",
                sortable: true,
                sortType: SortType.String,
                width: 400
            },
            {
                title: "Form Type",
                sortable: true,
                sortType: SortType.String,
                width: 420
            }
        ];
    }

    retrieveData() {
        this.loading = true;
        this.formBuilderService.getForms()
            .then((f) => {
                this.forms = angular.copy(f);
                this.createTableRows();
                this.checkJobCompletionType();
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private createTableRows() {
        this.tableRows = [];
        this.forms.forEach(form => {
            const row: IVeriTableRow<IForm> = {
                data: form,
                cells: this.createTableCells(form)
            };
            this.tableRows.push(row);
        });
    }

    private getFormTypeName(form: IForm): string {
        const unknownFormType = "Unknown form type!";
        if (form && (form.Type || form.Type === 0)) {
            const formTypeItem = this.formTypes.filter(element => +element.value === form.Type);
            if (formTypeItem && formTypeItem.length > 0) {
                return formTypeItem[0].name || unknownFormType;
            }
        }

        return unknownFormType;
    }

    private createTableCells(form: IForm): IVeriTableRowCell[] {

        this.formTypes = this.formBuilderService.getFormTypes(true);

        const formTypeName = this.getFormTypeName(form);

        const cells: IVeriTableRowCell[] = [
            { data: form, sortData: form.Name, contents: form.Name },
            { data: form, sortData: formTypeName, contents: formTypeName }
        ];

        return cells;
    }

    onRowClicked(row: IVeriTableRow<IForm>) {
        if (row && row.data) {
            this.selectedForm = row.data;
            this.selectedFormId = row.data.Id;
            this.createFormMode = false;
        }
    }

    isSelectedItem(formId: number): boolean {
        return this.selectedFormId === formId;
    }

    selectedFormCategoryDetails(category: string): IFormDetail[] {
        if (!this.forms || !this.selectedFormId) {
            return null;
        }

        const formDetails: IFormDetail[] =
            this.selectedFormDetails.filter(item => item.Category === category);

        return formDetails;
    }

    confirmCancelChanges(changesMade: boolean) {
        if (!changesMade) {
            this.cancelChanges();
            return;
        }
        const cancelChanges = this.$mdDialog
            .confirm()
            .title("Are you sure you wish to cancel these changes?")
            .ok("Yes")
            .cancel("No");

        this.$mdDialog
            .show(cancelChanges)
            .then(() => {
                this.cancelChanges();
            });
    }

    cancelChanges(): void {
        this.changesMade = false;
        this.createFormMode = false;
        this.selectedFormId = 0;
    }

    removeField(fieldId: number): void {
        const updatedDetails = this.selectedForm.FormFields
            .filter(x => x.FieldId !== fieldId);

        this.selectedForm.FormFields = updatedDetails;

        this.changesMade = true;
    }

    saveFormDetails(form: IForm, addMode: boolean): void {
        if (addMode) {
            this.addFormDetails(form);
        } else {
            this.updateFormDetails(form);
        }
    }

    updateFormDetails(form: IForm): void {
        this.saving = true;
        this.formBuilderService.updateForm(form)
            .then(() => {
                this.retrieveData();
            })
            .finally(() => {
                this.saving = false;
                this.selectedFormId = 0;
                this.selectedForm = null;
            });
    }

    addFormDetails(form: IForm): void {
        this.saving = true;
        this.formBuilderService.addForm(form)
            .then(() => {
                this.retrieveData();
            })
            .finally(() => {
                this.selectedFormId = 0;
                this.createForm = null;
                this.createFormMode = false;
                this.saving = false;
            });
    }

    createNewFormDetail() {
        const formDetail: IFormDetail = {
            FieldId: 0,
            Id: 0,
            Type: 0,
            Name: "",
            description: "",
            Validation: "",
            position: 0,
            Category: "",
            DefaultValue: "",
            isIdField: false
        };
        return formDetail;
    }

    createNewForm(): void {
        this.createForm = {
            Id: -1,
            Name: "",
            Description: "",
            Enabled: false,
            Type: 0,
            FormFields: []
        };

        this.createForm.FormFields.push(this.createNewFormDetail());

        this.selectedFormId = 0;
        this.createFormMode = true;
    }

    confirmDeleteForm() {
        const deleteConfirm = this.$mdDialog
            .confirm()
            .title("Are you sure you want to delete this form?")
            .ok("Delete")
            .cancel("Cancel");

        this.$mdDialog
            .show(deleteConfirm)
            .then(() => {
                this.deleteForm();
            });
    }

    deleteForm(): void {
        this.formBuilderService.removeForm(this.selectedForm)
            .then(() => {
                this.selectedFormId = 0;
                this.retrieveData();
            });
    }

    private checkJobCompletionType() {
        const jobCompletionType = 3;
        this.jobCompletionTypeFormId = -1;

        this.forms.forEach(form => {
            if (form.Type === jobCompletionType) {
                this.jobCompletionTypeFormId = form.Id;
            }
        });
    }
}

export class FormBuilderComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {};

        this.controller = FormBuilderController;
        this.template = require("./formBuilder.html");
    }
}
